import { SqlSchedulesService } from './sql-schedules.service';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Timestamp } from '@angular/fire/firestore';
import { FormsModule } from '@angular/forms';
import { Dialog } from '@capacitor/dialog';
import { LoadingController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { LoadingComponent } from '@scandium-oy/ngx-scandium';
import { addDays, addMinutes, isAfter, startOfDay, startOfToday } from 'date-fns';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, of } from 'rxjs';
import { debounceTime, filter, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { AppCommonModule } from '../common.module';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.dialog';
import { SelectedDayComponent } from '../day/selected-day/selected-day.component';
import { Schedule } from '../models/schedule.model';
import { IScheduleUser, Site } from '../models/site.model';
import { SubItem } from '../models/sub-item.model';
import { User } from '../models/user.model';
import { ClientsService } from '../services/clients.service';
import { ContractorsService } from '../services/contractors.service';
import { NavigationService } from '../services/navigation.service';
import { AddScheduleItem, SchedulesService } from '../services/schedules.service';
import { SitesService } from '../services/sites.service';
import { UsersService } from '../services/users.service';
import { WorkMachinesService } from '../services/work-machines.service';
import { getDateFromTimestamp } from '../utility/item';
import { memo } from '../utility/memo';
import { Roles } from '../utility/role';
import { fieldSorter, sortIgnoreCase } from '../utility/sort';
import { capitalizeFirstLetter } from '../utility/string';
import { convertTimestamp, isTimestamp, numberOfWeek } from '../utility/time';
import { ShiftDraftReviewService, ShiftDraftSnapshot } from '../work-force/shift-draft-review.service';
import { AllocationWorkspaceShiftEditContext } from './allocation-workspace.service';
import { ExtraPersonsComponent } from './components/extra-person/extra-person.component';
import { SignStatusComponent } from './components/sign-status/sign-status.component';
import { AddScheduleDialogComponent } from './dialog/add-schedule/add-schedule.dialog';
import { UserTicketsTodayDialogComponent } from './dialog/user-tickets/user-tickets.dialog';

function isFullHours(start: Date | Timestamp, end: Date | Timestamp) {
  if (!start || !end) {
 return true; 
}
  const from = isTimestamp(start) ? start.toDate() : new Date(start);
  const until = isTimestamp(end) ? end.toDate() : new Date(end);
  return until.getTime() - from.getTime() >= 8.5 * 3600000;
}

interface ScheduleClient {
  client: string;
  logo: string;
  name: string;
  sites: (Site & { guid: string; name: string })[];
}

@Component({
  selector: 'app-schedule',
  providers: [SqlSchedulesService, {provide: SchedulesService, useExisting: SqlSchedulesService}],
  templateUrl: './schedule.component.html',
  styleUrls: ['./schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    AppCommonModule,
    SignStatusComponent,
    ExtraPersonsComponent,
    SelectedDayComponent,
    FormsModule,
    LoadingComponent,
  ],
})
export class ScheduleComponent implements OnInit {
  readonly allocationError = inject(SqlSchedulesService).error;
  private navigationService = inject(NavigationService);
  private shiftDraftReviewService = inject(ShiftDraftReviewService);
  private users: User[];

  initialDate = input<Date | null>(null);
  initialUserGuid = input<string | null>(null);
  shiftEditContext = input<AllocationWorkspaceShiftEditContext | null>(null);

  selectedDay$ = new BehaviorSubject(startOfToday());

  weekNumber = signal<number>(null);
  loading = signal(false);
  dragOverSiteGuid = signal<string | null>(null);
  activeTargetSiteGuid = signal<string | null>(null);
  assignmentEditor = signal<{
    userGuid: string;
    userName: string;
    site: Site;
    date: Date;
    start: Date | null;
    end: Date | null;
    infoText: string;
    project: string;
  } | null>(null);

  editStart = signal('');
  editEnd = signal('');
  editInfoText = signal('');
  inlineSelectionHint = computed(() => this.activeTargetSiteGuid() ? this.translate.instant('schedule.selectWorkerHint') : null);

  private schedules$ = combineLatest([this.selectedDay$, toObservable(this.usersService.currentUserS)]).pipe(
    filter(([_, user]) => user != null),
    tap(() => this.loading.set(true)),
    switchMap(([date, user]) => this.schedulesService.getList({ date: date.toLocaleDateString('fi'), user: [Roles.worker].includes(user.role) ? user.guid : null })),
    debounceTime(100),
    tap(() => this.loading.set(false)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private workingUsers$ = this.schedules$.pipe(
    map((schedules) => schedules.map((s) => s.times.filter((t) => {
      const duration = isFullHours(t.start, t.end);
      return duration;
    }).map((t) => t.user))),
    map((users) => users.flat()),
  );

  private workingMachines$ = this.schedules$.pipe(
    map((schedules) => schedules.map((s) => s.machines.map((m) => m.machine))),
    map((items) => items.flat()),
  );

  clients$: Observable<ScheduleClient[]> = this.sitesService.getActiveList().pipe(
    filter((sites) => sites?.length > 0),
    debounceTime(250),
    map((sites) => sites.map((site) => this.prepareSite(site))),
    map((sites) => {
      const clients = new Set<string>(sites.map((it) => it.client));
      return Array.from(clients).map((client) => {
        const clientSites = sites.filter((it) => it.client === client).sort((a, b) => sortIgnoreCase(a, b, 'name')).map((s) => Object.assign(s, { guid: s.guid, name: s.project }));
        const clientObj = this.clientsService.getSync(client);
        const name = clientObj?.name ?? client ?? '';
        return { client, name, logo: clientObj?.logo, sites: clientSites };
      });
    }),
    map((clients) => clients.sort((a, b) => sortIgnoreCase(a, b, 'name'))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  roleS = computed(() => this.usersService.role());
  showAllUsers = signal(false);

  workers$ = combineLatest([this.constractorsService.getCurrentContractor(), toObservable(this.usersService.currentUserS), toObservable(this.showAllUsers)]).pipe(
    filter(([contractor, user]) => contractor != null && user != null),
    switchMap(([contractor, user, showAllUsers]) => [Roles.worker].includes(user.role) ?
      of([user]) : this.usersService.getListFromDb({ roles: [Roles.worker, Roles.supervisor, Roles.admin], contractor: contractor.guid }).pipe(
        map((users) => users.filter((it) => showAllUsers || !it.inactive)),
      )),
    tap((users) => this.users = users),
    map((workers) => workers.map((w) => {
      w.displayName = capitalizeFirstLetter(w.displayName);
      return w;
    })),
    map((workers) => workers.sort(fieldSorter(['displayName']))),
    switchMap((users) => combineLatest([this.workingUsers$, of(users)])),
    map(([working, users]) => users.filter((u) => !working.includes(u.guid))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  isLocked = memo((site: Site, user: IScheduleUser) => site.schedule?.locked?.includes(user.guid));

  constructor(
    private clientsService: ClientsService,
    private constractorsService: ContractorsService,
    private loadingCtrl: LoadingController,
    private modalCtrl: ModalController,
    private sitesService: SitesService,
    private schedulesService: SchedulesService,
    private translate: TranslateService,
    private usersService: UsersService,
    private workMachinesService: WorkMachinesService,
  ) { }

  private toDate(value: Date | Timestamp | null | undefined): Date | null {
    const date = convertTimestamp(value);
    return date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
  }

  private toTimeValue(value: Date | Timestamp | null | undefined): string {
    const date = this.toDate(value);
    if (!date) {
      return '';
    }
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`;
  }

  private createDraftSnapshot(
    userGuid: string,
    userName: string,
    site: Site,
    date: Date,
    start: Date | Timestamp | null,
    end: Date | Timestamp | null,
    infoText: string,
    project: string,
    status: 'draft' | 'published',
    absence: string | null = null,
  ): ShiftDraftSnapshot {
    return {
      contractorGuid: site.contractor ?? this.constractorsService.contractorS()?.guid ?? null,
      dateKey: date.toLocaleDateString('fi'),
      date: startOfDay(date),
      userGuid,
      userName,
      siteGuid: site.guid,
      siteName: site.name ?? site.project ?? site.guid,
      start: this.toDate(start),
      end: this.toDate(end),
      infoText,
      project,
      status,
      absence,
    };
  }

  private combineDateAndTime(date: Date, timeValue: string, fallback: Date | null): Date {
    const source = fallback ? new Date(fallback) : startOfDay(date);
    if (!timeValue) {
      return source;
    }
    const [hours, minutes] = timeValue.split(':').map((value) => Number(value));
    source.setHours(hours || 0, minutes || 0, 0, 0);
    return source;
  }

  private prepareSite(site: Site): Site {
    site.workers$ = this.selectedDay$.pipe(
      map((date) => date.toLocaleDateString('fi')),
      switchMap((date) => this.schedulesService.getSingle({ site: site.guid, date })),
      debounceTime(100),
      tap((schedule) => site.schedule = schedule),
      switchMap((schedule) => schedule ?
        this.usersService.getByGuids(schedule.users).pipe(
          map((workers) => workers.sort(fieldSorter(['displayName']))),
          map((workers) => workers.map((worker) => {
            const machine = schedule.machines?.find((m) => m.user === worker.guid);
            return {
              guid: worker.guid,
              displayName: worker.displayName,
              information: schedule.information?.find((info) => info.user === worker.guid)?.text,
              machine$: machine?.machine ? this.workMachinesService.get(machine.machine) : of(null),
              lang: worker.lang,
            };
          })),
        )
        : of([])),
      debounceTime(100),
    );
    return site;
  }

  private async handleAddWorker(user: User, site: Site, schedule: Schedule) {
    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.wait'),
      spinner: 'crescent',
    });
    loading.present();

    const machines = this.workingMachines$.pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    const date = this.selectedDay$.value;
    this.modalCtrl.create({
      component: AddScheduleDialogComponent,
      componentProps: { user, site, date, machines },
      cssClass: ['stack-modal'],
    }).then((m) => {
      loading.dismiss();
      m.present();

      m.onDidDismiss<AddScheduleItem>().then(async (data) => {
        if (data.data) {
          await this.schedulesService.handleAddSchedule(data.data, user, schedule);
          this.shiftDraftReviewService.recordAddition(this.createDraftSnapshot(
            user.guid,
            user.displayName,
            data.data.site,
            data.data.startDate,
            data.data.start,
            data.data.end,
            data.data.text,
            data.data.project,
            'draft',
            data.data.absence ?? null,
          ));
        }
      });
    });
  }

  private selectUser(userGuid: string, site: Site) {
    const schedule = site.schedule;
    const user = this.users.find((u) => u.guid === userGuid);
    this.handleAddWorker(user, site, schedule);
  }

  private assignSelectedWorkerToActiveSite(userGuid: string) {
    const siteGuid = this.activeTargetSiteGuid();
    if (!siteGuid) {
      return false;
    }

    this.clients$.pipe(
      take(1),
      map((clients) => clients.flatMap((client) => client.sites).find((site) => site.guid === siteGuid)),
    ).subscribe((site) => {
      if (site) {
        this.selectUser(userGuid, site);
      }
      this.activeTargetSiteGuid.set(null);
    });

    return true;
  }

  private async applyShiftEditContext(): Promise<void> {
    const editContext = this.shiftEditContext();
    if (!editContext) {
      return;
    }

    this.changeFullDate(editContext.date);

    const [site, schedule] = await Promise.all([
      firstValueFrom(this.clients$.pipe(
        take(1),
        map((clients) => clients.flatMap((client) => client.sites).find((item) => item.guid === editContext.siteGuid)),
      )),
      firstValueFrom(this.schedulesService.getSingleOnce({
        site: editContext.siteGuid,
        date: editContext.date.toLocaleDateString('fi'),
      })),
    ]);

    if (!site || !schedule) {
      this.activeTargetSiteGuid.set(editContext.siteGuid);
      return;
    }

    site.schedule = schedule;

    const time = schedule.times.find((item) => item.user === editContext.userGuid) ?? null;
    const information = schedule.information?.find((item) => item.user === editContext.userGuid)?.text ?? '';
    const project = schedule.projects?.find((item) => item.user === editContext.userGuid)?.project ?? '';
    this.openAssignmentEditor({
      userGuid: editContext.userGuid,
      userName: editContext.userName,
      site,
      date: editContext.date,
      start: this.toDate(time?.start),
      end: this.toDate(time?.end),
      infoText: information,
      project,
    });
  }

  ngOnInit(): void {
    const initialDate = this.initialDate();
    if (initialDate) {
      this.selectedDay$.next(startOfDay(initialDate));
    }
    this.weekNumber.set(numberOfWeek(this.selectedDay$.value));
    void this.applyShiftEditContext();
  }

  onSite(guid: string) {
    this.navigationService.navigateToSite(guid);
  }

  changeFullDate(date: Date) {
    this.selectedDay$.next(date);
    this.weekNumber.set(numberOfWeek(startOfDay(date)));
  }

  changeDate(value: number): void {
    const selected = this.selectedDay$.value;
    const date = addDays(selected, value);
    this.changeFullDate(date);
  }

  hasInformation(schedule: Schedule, userGuid: string) {
    return schedule?.information?.find((it) => it.user === userGuid)?.text;
  }

  allowDrop(e) {
    e.preventDefault();
  }

  drag(event) {
    event.dataTransfer.setData('text', event.target.id);
  }

  drop(e: DragEvent, site: Site) {
    e.preventDefault();
    this.dragOverSiteGuid.set(null);
    this.activeTargetSiteGuid.set(null);
    const data = e.dataTransfer.getData('text');
    this.selectUser(data, site);
  }

  onDragEnter(siteGuid: string) {
    this.dragOverSiteGuid.set(siteGuid);
  }

  onDragLeave(siteGuid: string) {
    if (this.dragOverSiteGuid() === siteGuid) {
      this.dragOverSiteGuid.set(null);
    }
  }

  addWorker(site: Site) {
    this.activeTargetSiteGuid.set(site.guid);
  }

  clearActiveTarget() {
    this.activeTargetSiteGuid.set(null);
  }

  onUser(user: User) {
    if (this.assignSelectedWorkerToActiveSite(user.guid)) {
      return;
    }
    this.navigationService.navigateToUser(user.guid);
  }

  openAssignmentEditorForUser(user: IScheduleUser, site: Site, date: Date): void {
    const time = site.schedule?.times?.find((item) => item.user === user.guid) ?? null;
    const infoText = site.schedule?.information?.find((item) => item.user === user.guid)?.text ?? '';
    const project = site.schedule?.projects?.find((item) => item.user === user.guid)?.project ?? '';

    this.openAssignmentEditor({
      userGuid: user.guid,
      userName: user.displayName,
      site,
      date,
      start: time?.start ?? null,
      end: time?.end ?? null,
      infoText,
      project,
    });
  }

  openAssignmentEditor(item: {
    userGuid: string;
    userName: string;
    site: Site;
    date: Date;
    start: Date | Timestamp | null;
    end: Date | Timestamp | null;
    infoText: string;
    project: string;
  }): void {
    this.assignmentEditor.set({
      ...item,
      start: this.toDate(item.start),
      end: this.toDate(item.end),
    });
    this.editStart.set(this.toTimeValue(item.start));
    this.editEnd.set(this.toTimeValue(item.end));
    this.editInfoText.set(item.infoText ?? '');
    this.activeTargetSiteGuid.set(item.site.guid);
  }

  closeAssignmentEditor(): void {
    this.assignmentEditor.set(null);
  }

  updateEditInfoText(value: string): void {
    this.editInfoText.set(value);
  }

  async saveAssignmentEdit(): Promise<void> {
    const editor = this.assignmentEditor();
    if (!editor?.site?.schedule) {
      return;
    }

    const currentTime = editor.site.schedule.times.find((time) => time.user === editor.userGuid) ?? null;
    const currentInfoText = editor.site.schedule.information?.find((info) => info.user === editor.userGuid)?.text ?? '';
    const currentProject = editor.site.schedule.projects?.find((project) => project.user === editor.userGuid)?.project ?? '';
    const beforeSnapshot = currentTime ? this.createDraftSnapshot(
      editor.userGuid,
      editor.userName,
      editor.site,
      editor.date,
      currentTime.start,
      currentTime.end,
      currentInfoText,
      currentProject,
      currentTime.status ?? 'published',
      currentTime.absence ?? null,
    ) : null;

    const schedule = this.schedulesService.updateSchedule(editor.site.schedule, {
      days: 1,
      text: this.editInfoText(),
      machine: '',
      project: editor.project,
      start: this.combineDateAndTime(editor.date, this.editStart(), editor.start),
      end: this.combineDateAndTime(editor.date, this.editEnd(), editor.end),
      contractor: editor.site.schedule.contractor,
      startDate: editor.date,
      includeWeekend: false,
      site: editor.site,
      status: 'draft',
      absence: null,
    }, {
      guid: editor.userGuid,
      name: editor.userName,
    } as SubItem);

    await this.schedulesService.update(schedule);
    const afterSnapshot = this.createDraftSnapshot(
      editor.userGuid,
      editor.userName,
      editor.site,
      editor.date,
      this.combineDateAndTime(editor.date, this.editStart(), editor.start),
      this.combineDateAndTime(editor.date, this.editEnd(), editor.end),
      this.editInfoText(),
      editor.project,
      'draft',
      null,
    );
    if (beforeSnapshot) {
      this.shiftDraftReviewService.recordUpdate(beforeSnapshot, afterSnapshot);
    } else {
      this.shiftDraftReviewService.recordAddition(afterSnapshot);
    }
    this.closeAssignmentEditor();
  }

  async deleteAssignmentEdit(): Promise<void> {
    const editor = this.assignmentEditor();
    if (!editor?.site?.schedule) {
      return;
    }
    const currentTime = editor.site.schedule.times.find((time) => time.user === editor.userGuid) ?? null;
    const currentInfoText = editor.site.schedule.information?.find((info) => info.user === editor.userGuid)?.text ?? '';
    const currentProject = editor.site.schedule.projects?.find((project) => project.user === editor.userGuid)?.project ?? '';
    if (currentTime) {
      this.shiftDraftReviewService.recordRemoval(this.createDraftSnapshot(
        editor.userGuid,
        editor.userName,
        editor.site,
        editor.date,
        currentTime.start,
        currentTime.end,
        currentInfoText,
        currentProject,
        currentTime.status ?? 'published',
        currentTime.absence ?? null,
      ));
    }
    const schedule = this.schedulesService.removeUser(editor.site.schedule, editor.userGuid);
    await this.schedulesService.update(schedule);
    this.closeAssignmentEditor();
  }

  private remove(user: IScheduleUser, schedule: Schedule) {
    setTimeout(async () => {
      const existingTime = schedule.times.find((time) => time.user === user.guid) ?? null;
      const existingInfo = schedule.information?.find((info) => info.user === user.guid)?.text ?? '';
      const existingProject = schedule.projects?.find((project) => project.user === user.guid)?.project ?? '';
      const site = this.sitesService.getSync(schedule.site);
      if (existingTime && site) {
        this.shiftDraftReviewService.recordRemoval(this.createDraftSnapshot(
          user.guid,
          user.displayName,
          site,
          getDateFromTimestamp(schedule.dateObj),
          existingTime.start,
          existingTime.end,
          existingInfo,
          existingProject,
          existingTime.status ?? 'published',
          existingTime.absence ?? null,
        ));
      }
      const loading = await this.loadingCtrl.create({
        message: this.translate.instant('general.wait'),
        spinner: 'crescent',
      });
      loading.present();
      schedule = this.schedulesService.removeUser(schedule, user.guid);
      this.schedulesService.update(schedule).finally(() => {
        loading.dismiss();
      });
    }, 500);
  }

  removeWorker(user: IScheduleUser, schedule: Schedule) {
    this.schedulesService.getListOnce({ user: user.guid }).pipe(
      take(1),
    ).subscribe((schedules) => {
      if (isTimestamp(schedule.dateObj)) {
        schedule.dateObj = schedule.dateObj.toDate();
      }
      const futureSchedules = schedules.filter((it) => isAfter(getDateFromTimestamp(it.dateObj), addMinutes(getDateFromTimestamp(schedule.dateObj), -1)));
      if (futureSchedules.length > 1) {
        this.modalCtrl.create({
          component: ConfirmDialogComponent, componentProps: {
            title: this.translate.instant('general.delete'),
            text: this.translate.instant('calendar.removeThisOrAllSchedules'),
            buttons: [this.translate.instant('general.cancel'), this.translate.instant('calendar.onlyThis'), this.translate.instant('tickets.all')],
          },
          cssClass: ['stack-modal'],
        }).then((m) => {
          m.present();
          m.onDidDismiss().then((data) => {
            if (data.data) {
              if (data.data === 0) {
                return;
              } else if (data.data === 1) {
                this.remove(user, schedule);
              } else if (data.data === 2) {
                // Remove all
                futureSchedules.map((s) => this.remove(user, s));
              }
            }
          });
        });
      } else if (futureSchedules.length === 1) {
        this.remove(user, schedule);
      }
    });
  }

  onTicketList(user: IScheduleUser, site: Site) {
    this.modalCtrl.create({ component: UserTicketsTodayDialogComponent, componentProps: { user, site, date: this.selectedDay$.getValue() } }).then((m) => {
      m.present();
    });
  }

  onLock(user: IScheduleUser, site: Site) {
    const schedule = site.schedule;
    if (schedule.locked?.includes(user.guid)) {
      Dialog.confirm({
        message: this.translate.instant('schedule.unlock', { user: user.displayName, site: site.name }),
        okButtonTitle: this.translate.instant['general.yes'],
        cancelButtonTitle: this.translate.instant['general.no'],
      }).then((dialog) => {
        if (dialog.value) {
          schedule.locked = schedule.locked.filter((s) => s !== user.guid);
          this.schedulesService.update(schedule);
        }
      });
    } else {
      Dialog.confirm({
        message: this.translate.instant('schedule.lock', { user: user.displayName, site: site.name }),
        okButtonTitle: this.translate.instant['general.yes'],
        cancelButtonTitle: this.translate.instant['general.no'],
      }).then((dialog) => {
        if (dialog.value) {
          // Lock
          if (schedule.locked == null) {
            schedule.locked = [];
          }
          schedule.locked.push(user.guid);
          this.schedulesService.update(schedule);
        }
      });
    }
  }
}
